// One read for every Process field of a form on a run page (z8nrz7e8kw).
//
// Each Process field asked for its own connection, its own Assign To roster and
// (with equipment) its own live holds: three requests per field, each paying the
// API's auth/tenant/permission lookups, and the cards showed "No process
// configured" until their own answer came back. The form now asks once per kind
// and puts each field's answer in the cache entry its hook reads, so the hooks
// find their data already there and every refresh after a write keeps working
// exactly as before (it still targets the per-field keys).

import type { QueryClient } from '@tanstack/react-query'
import type {
  EquipmentReservation,
  ProcessAssignableUser,
  ProcessStepConnection,
} from '@/api/processStepsApi'
import { allFields } from '@/lib/formSections'
import { connectionKey, processAssignableUsersKey, reservationsKey } from '@/lib/processStepKeys'
import type { FormDefinition } from '@/types/cgt'

/** The connection keys of a form's Process fields: `libraryFieldId ?? id`, as ProcessStepField resolves them. */
export function processFieldKeysOf(definition: FormDefinition): string[] {
  const keys = new Set<string>()
  for (const field of allFields(definition)) {
    if (field.fieldType === 'process') keys.add(field.libraryFieldId ?? field.id)
  }
  return [...keys]
}

/** Each part null when its read failed (e.g. an older backend): those fields fall back to their own reads. */
export interface ProcessRunBatch {
  connections: Record<string, ProcessStepConnection | null> | null
  rosters: Record<string, ProcessAssignableUser[]> | null
  reservations: Record<string, EquipmentReservation[]> | null
}

/** The three batch reads this needs; `processStepsApi` in the app, a fake in tests. */
export interface ProcessRunBatchApi {
  getMany: (tenantId: string, fieldIds: readonly string[], submissionId?: string) => Promise<Record<string, ProcessStepConnection | null>>
  listAssignableUsersByField: (tenantId: string, fieldIds: readonly string[]) => Promise<Record<string, ProcessAssignableUser[]>>
  listReservationsByField: (tenantId: string, submissionId: string, fieldIds: readonly string[]) => Promise<Record<string, EquipmentReservation[]>>
}

/**
 * Read a form's process data in (at most) three requests. The roster and the
 * holds are only read for someone who may execute steps — the routes need
 * `process.execute`, and the per-field hooks are gated on it the same way.
 */
export async function loadProcessRunBatch(
  api: ProcessRunBatchApi,
  tenantId: string,
  submissionId: string,
  fieldIds: readonly string[],
  canExecute: boolean,
): Promise<ProcessRunBatch> {
  const [connections, rosters, reservations] = await Promise.allSettled([
    api.getMany(tenantId, fieldIds, submissionId),
    canExecute ? api.listAssignableUsersByField(tenantId, fieldIds) : Promise.reject(new Error('not executable')),
    canExecute ? api.listReservationsByField(tenantId, submissionId, fieldIds) : Promise.reject(new Error('not executable')),
  ])
  return {
    connections: connections.status === 'fulfilled' ? connections.value : null,
    rosters: rosters.status === 'fulfilled' ? rosters.value : null,
    reservations: reservations.status === 'fulfilled' ? reservations.value : null,
  }
}

/**
 * Put each field's answer where its hook looks for it. A field missing from a
 * part's answer is left alone, so its hook reads it for itself.
 */
export function seedProcessRunBatch(
  queryClient: QueryClient,
  tenantId: string,
  submissionId: string,
  fieldIds: readonly string[],
  batch: ProcessRunBatch,
): void {
  for (const fieldId of fieldIds) {
    if (batch.connections && fieldId in batch.connections) {
      queryClient.setQueryData(connectionKey(tenantId, fieldId, submissionId), batch.connections[fieldId] ?? null)
    }
    if (batch.rosters && fieldId in batch.rosters) {
      queryClient.setQueryData(processAssignableUsersKey(tenantId, fieldId), batch.rosters[fieldId])
    }
    if (batch.reservations && fieldId in batch.reservations) {
      queryClient.setQueryData(reservationsKey(tenantId, submissionId, fieldId), batch.reservations[fieldId])
    }
  }
}
